import { graphql } from "@acto/gql";
import type { GetTeamMembersGroupedByRegionQuery, GetTeamMembersQuery } from "@acto/gql/graphql";
import { DateRangeInput, EmployeeFilter, EmployeeOrderBy, TodoType } from "@acto/gql/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "urql";
import { TeamEmployee, TeamKpiSummary } from "@acto/taskpane/core/domain";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";

const TeamEmployeesQuery = graphql(`
  query GetTeamMembers(
    $dateRange: DateRangeInput!
    $first: Int!
    $after: String
    $orderBy: EmployeeOrderBy
    $filter: EmployeeFilter
    $todoTypes: [TodoType!]
    $withAppointments: Boolean! = false
    $withAppointmentStates: Boolean! = false
    $withMarkedDone: Boolean! = false
  ) {
    team {
      members(
        first: $first
        after: $after
        orderBy: $orderBy
        dateRange: $dateRange
        filter: $filter
        todoTypes: $todoTypes
      ) {
        edges {
          cursor
          node {
            name
            employee {
              id
              name
              region
            }
            meetings {
              edges {
                node {
                  id
                  scheduledDate
                  scheduledStartTime
                  scheduledEndTime
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  note {
                    id
                    summary
                    content
                    openTodosCount
                  }
                }
              }
            }
            appointments @include(if: $withAppointments) {
              edges {
                node {
                  id
                  scheduledDate
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  note {
                    id
                    summary
                    content
                    openTodosCount
                  }
                  state
                  cancellation {
                    reason
                    comment
                  }
                  observation {
                    id
                  }
                  series {
                    id
                  }
                }
              }
            }
            dismissals {
              edges {
                node {
                  id
                  dismissedAt
                  dismissedUntil
                  client {
                    __typename
                    id
                    name
                    externalReference
                    ... on Client {
                      city
                      postcode
                      industry
                      todaysRecommendedObservation {
                        title {
                          locale
                          value
                        }
                      }
                      lastVisitDate
                    }
                  }
                  answer {
                    reason
                    comment
                  }
                }
              }
            }
            markedDone @include(if: $withMarkedDone) {
              edges {
                node {
                  id
                  markedDoneAt
                  channel
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  report {
                    id
                    summary
                    content
                  }
                }
              }
            }
            counts {
              meetings
              recaps
              dismissals
              openTodos
              markedDone
              meetingStates @include(if: $withAppointmentStates) {
                planned
                documented
                noReport
                didNotTakePlace
              }
            }
          }
          revenue {
            totalValue
            previousTotalValue
            percentageChange
          }
          margin {
            totalValue
            previousTotalValue
            percentageChange
          }
          profit {
            totalValue
            previousTotalValue
            percentageChange
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totals {
          revenue {
            totalValue
            previousTotalValue
            percentageChange
          }
          margin {
            totalValue
            previousTotalValue
            percentageChange
          }
          profit {
            totalValue
            previousTotalValue
            percentageChange
          }
          activityMetrics {
            meetingsCount
            recapsCount
            dismissalsCount
            openTodosCount
            meetingStates @include(if: $withAppointmentStates) {
              planned
              documented
              noReport
              didNotTakePlace
            }
          }
          dateRanges {
            previous {
              startDate
              endDate
            }
          }
        }
      }
    }
  }
`);

const TeamEmployeesGroupedByRegionQuery = graphql(`
  query GetTeamMembersGroupedByRegion(
    $dateRange: DateRangeInput!
    $first: Int!
    $after: String
    $orderBy: EmployeeOrderBy
    $filter: EmployeeFilter
    $todoTypes: [TodoType!]
    $withAppointments: Boolean! = false
    $withAppointmentStates: Boolean! = false
    $withMarkedDone: Boolean! = false
  ) {
    team {
      members(
        first: $first
        after: $after
        orderBy: $orderBy
        dateRange: $dateRange
        filter: $filter
        todoTypes: $todoTypes
        groupByRegion: true
      ) {
        edges {
          cursor
          node {
            name
            employee {
              id
              name
              region
            }
            meetings {
              edges {
                node {
                  id
                  scheduledDate
                  scheduledStartTime
                  scheduledEndTime
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  note {
                    id
                    summary
                    content
                    openTodosCount
                  }
                }
              }
            }
            appointments @include(if: $withAppointments) {
              edges {
                node {
                  id
                  scheduledDate
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  note {
                    id
                    summary
                    content
                    openTodosCount
                  }
                  state
                  cancellation {
                    reason
                    comment
                  }
                  observation {
                    id
                  }
                  series {
                    id
                  }
                }
              }
            }
            dismissals {
              edges {
                node {
                  id
                  dismissedAt
                  dismissedUntil
                  client {
                    __typename
                    id
                    name
                    externalReference
                    ... on Client {
                      city
                      postcode
                      industry
                      todaysRecommendedObservation {
                        title {
                          locale
                          value
                        }
                      }
                      lastVisitDate
                    }
                  }
                  answer {
                    reason
                    comment
                  }
                }
              }
            }
            markedDone @include(if: $withMarkedDone) {
              edges {
                node {
                  id
                  markedDoneAt
                  channel
                  client {
                    __typename
                    id
                    name
                    externalReference
                  }
                  report {
                    id
                    summary
                    content
                  }
                }
              }
            }
            counts {
              meetings
              recaps
              dismissals
              openTodos
              markedDone
              meetingStates @include(if: $withAppointmentStates) {
                planned
                documented
                noReport
                didNotTakePlace
              }
            }
          }
          revenue {
            totalValue
            previousTotalValue
            percentageChange
          }
          margin {
            totalValue
            previousTotalValue
            percentageChange
          }
          profit {
            totalValue
            previousTotalValue
            percentageChange
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        regionGroups {
          region
          memberCount
        }
        totals {
          revenue {
            totalValue
            previousTotalValue
            percentageChange
          }
          margin {
            totalValue
            previousTotalValue
            percentageChange
          }
          profit {
            totalValue
            previousTotalValue
            percentageChange
          }
          activityMetrics {
            meetingsCount
            recapsCount
            dismissalsCount
            openTodosCount
            meetingStates @include(if: $withAppointmentStates) {
              planned
              documented
              noReport
              didNotTakePlace
            }
          }
          dateRanges {
            previous {
              startDate
              endDate
            }
          }
        }
      }
    }
  }
`);

type EmployeeEdge = NonNullable<NonNullable<GetTeamMembersQuery["team"]>["members"]>["edges"][number];
type GroupedConnection = NonNullable<NonNullable<GetTeamMembersGroupedByRegionQuery["team"]>["members"]>;

interface UseTeamEmployeesOptions {
  dateRange: DateRangeInput;
  pageSize?: number;
  orderBy?: EmployeeOrderBy;
  searchTerm?: string;
  employeeId?: string;
  todoTypes?: TodoType[];
  withAppointments?: boolean;
  withAppointmentStates?: boolean;
  withMarkedDone?: boolean;
  groupByRegion?: boolean;
}

export interface PreviousDateRange {
  startDate: string;
  endDate: string;
}

export interface RegionGroup {
  region: string | null;
  memberCount: number;
}

export interface UseTeamEmployeesResult extends PaginatedDataAccessResult<TeamEmployee> {
  totals?: TeamKpiSummary;
  previousRange?: PreviousDateRange;
  regionGroups?: RegionGroup[];
}

const DEFAULT_PAGE_SIZE = 10;

export function useTeamEmployees({
  dateRange,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy,
  searchTerm,
  employeeId,
  todoTypes,
  withAppointments = false,
  withAppointmentStates = false,
  withMarkedDone = false,
  groupByRegion = false,
}: UseTeamEmployeesOptions): UseTeamEmployeesResult {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const [pageVariables, setPageVariables] = useState<{ first: number; after: string | null }[]>([
    { first: pageSize, after: null },
  ]);
  const [allEdges, setAllEdges] = useState<EmployeeEdge[]>([]);

  const stableKey = useMemo(
    () =>
      JSON.stringify({
        dateRange,
        orderBy,
        searchTerm,
        employeeId,
        todoTypes,
        comparisonMode,
        withAppointments,
        withAppointmentStates,
        withMarkedDone,
        groupByRegion,
      }),
    [
      dateRange,
      orderBy,
      searchTerm,
      employeeId,
      todoTypes,
      comparisonMode,
      withAppointments,
      withAppointmentStates,
      withMarkedDone,
      groupByRegion,
    ],
  );

  const filter: EmployeeFilter = useMemo(
    () => ({
      comparisonMode,
      ...(searchTerm && { searchTerm }),
      ...(employeeId && { id: employeeId }),
    }),
    [comparisonMode, searchTerm, employeeId],
  );
  const currentVariables = pageVariables[pageVariables.length - 1];

  useEffect(() => {
    setPageVariables([{ first: pageSize, after: null }]);
    setAllEdges([]);
  }, [pageSize, stableKey]);

  const [result, reexecuteQuery] = useQuery({
    query: groupByRegion ? TeamEmployeesGroupedByRegionQuery : TeamEmployeesQuery,
    variables: {
      dateRange,
      first: currentVariables.first,
      after: currentVariables.after,
      orderBy,
      filter,
      todoTypes,
      withAppointments,
      withAppointmentStates,
      withMarkedDone,
    },
    requestPolicy: "cache-first",
  });

  const { data, fetching: isFetching, error } = result;
  const isPending = isFetching && !data;
  const connection = data?.team?.members;
  const edges = connection?.edges ?? [];
  const hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
  const endCursor = connection?.pageInfo?.endCursor ?? null;
  const totalsData = connection?.totals;

  useEffect(() => {
    if (isFetching || !connection) return;

    setAllEdges((prev) => {
      const newEdges = edges.filter((edge) => edge !== null && edge !== undefined);

      if (pageVariables.length === 1) {
        return newEdges;
      }

      const expectedCountBeforeLastPage = pageVariables.slice(0, -1).reduce((sum, page) => sum + page.first, 0);

      if (prev.length > expectedCountBeforeLastPage) {
        return [...prev.slice(0, expectedCountBeforeLastPage), ...newEdges];
      } else {
        return [...prev, ...newEdges];
      }
    });
  }, [connection, isFetching, pageVariables, edges]);

  const fetchNextPage = useCallback(() => {
    if (isFetching || !hasNextPage) return;
    setPageVariables((prev) => [...prev, { first: pageSize, after: endCursor }]);
  }, [isFetching, hasNextPage, endCursor, pageSize]);

  const refreshAfter = useCallback(
    (id: string) => {
      const itemIndex = allEdges.findIndex((e) => e.node.employee.id === id);
      if (itemIndex === -1) {
        setPageVariables([{ first: pageSize, after: null }]);
        return;
      }

      let itemCount = 0;
      let targetPageIndex = 0;
      for (let i = 0; i < pageVariables.length; i++) {
        if (itemIndex < itemCount + pageVariables[i].first) {
          targetPageIndex = i;
          break;
        }
        itemCount += pageVariables[i].first;
      }

      const pagesToKeep = pageVariables.slice(0, targetPageIndex);
      const pagesToCollapse = pageVariables.slice(targetPageIndex);
      const collapsedSize = pagesToCollapse.reduce((sum, page) => sum + page.first, 0);
      const collapsedAfter = pageVariables[targetPageIndex].after;

      setPageVariables([...pagesToKeep, { after: collapsedAfter, first: collapsedSize }]);
    },
    [allEdges, pageVariables, pageSize],
  );

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  const employees = useMemo(() => allEdges.map((edge) => TeamEmployee.fromEdge(edge)), [allEdges]);
  const totals = useMemo(() => (totalsData ? TeamKpiSummary.fromTotals(totalsData) : undefined), [totalsData]);
  const previousRange = totalsData?.dateRanges?.previous ?? undefined;
  const regionGroupsData = groupByRegion ? (connection as GroupedConnection | undefined)?.regionGroups : undefined;
  const regionGroups = useMemo(
    () => regionGroupsData?.map((group) => ({ region: group.region ?? null, memberCount: group.memberCount })),
    [regionGroupsData],
  );

  return {
    data: employees,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
    totals,
    previousRange,
    regionGroups,
  };
}
